import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';

import { RiArrowLeftSLine } from 'react-icons/ri';
import { Countdown } from 'components/countdown';
import { ExportButton } from 'components/generate-xcel';
import ModalChangeIndicator from 'components/modal-change-indicator';
import CartoesCarregando from 'components/ui/cartoes-carregando';
import { NotFound } from 'pages/NotFound';
import { etapasDoCiclo } from 'pages/MinhaEquipe/calendario-do-ciclo';
import CalendarioDoCiclo from 'pages/MinhaEquipe/components/CalendarioDoCiclo';
import { periodoDa } from 'pages/RealizacaoDestaque/components/AgendaDaRealizacao';
import CartaoDaFila from 'pages/RealizacaoDestaque/components/CartaoDaFila';
import { IndicadorDaFila } from 'pages/RealizacaoDestaque/components/IndicadoresDaFila';
import SecaoDaFila, { CLASSES_DO_EXPORTAR } from 'pages/RealizacaoDestaque/components/SecaoDaFila';
import SeletoresDaFila from 'pages/RealizacaoDestaque/components/SeletoresDaFila';
import TopoDoGestor from 'pages/RealizacaoDestaque/components/TopoDoGestor';
import VazioDaFila from 'pages/RealizacaoDestaque/components/VazioDaFila';
import { OrdemDaFila, ordenarFila } from 'pages/RealizacaoDestaque/ordenar-fila';
import { getTasksManagerNormal } from 'services/dashboard.service';
import { GetVersion } from 'services/utils';
import { getFutureCalendar, getMonthNameFromString } from 'utils/helpers';
import { CheckManagerType } from 'utils/strings';

import ModalAlert from '../../components/ModalAlert';
import ModalCod5 from '../../components/ModalCod5';
import SelectGestor from '../../components/SelectGestor';

import { setFlowChoice } from '../shared/eligibility';
import { FlowLoading } from '../shared/FlowLoading';
import { canUpdateIndicator } from '../shared/indicator';
import { getPlayerInfoWithPairs } from '../shared/playerInfoCache';
import { FlowTabs } from '../shared/FlowTabs';
import { matchesSearch } from '../shared/search';
import { useCycleStage } from '../shared/useCycleStage';
import { useFlowEligibility } from '../shared/useFlowEligibility';
import { useLateralidade } from '../shared/useLateralidade';
import { useServerTime } from 'hooks/useServerTime';
import { useTasksData } from '../shared/useTasksData';
import { buildNormalContext, NormalRuleContext, userCodEtapa } from './context';
import { computeQuotas, filterStageData } from './quotas';
import {
  canApproveParticipantsCount,
  canAward,
  canDouble,
  canEdit,
  canEvaluate,
  canPromote,
  canReset,
  getPlayerStep,
  getTaskMonthStep2Start,
  getTaskStatuses,
  getTaskStepLocal,
  hasTaskFeedbackForUser,
} from './rules';
import { useNormalActions } from './useNormalActions';

// Página do fluxo Normal (Diretoria/Rede) — port do monólito
// src/pages/MinhaEquipe/ApreciarRealizacao/index.tsx restrito ao universo NÃO-MAD
// (aggregate tasks_manager_normal). As abas diretoria×rede continuam internas a este
// fluxo; a divisão Normal×MAD é feita pelas rotas (ELE-02/04) e a aba MAD do FlowTabs
// navega para a página do outro fluxo.

interface FluxoNormalProps {
  tab?: 'diretoria' | 'rede';
}

export function FluxoNormal({ tab }: FluxoNormalProps) {
  const location = useLocation();

  const { user, cyclesExtended, realCycle, cycleSelected, setCycleSelected, userInfo, cycles } = useAuth();

  const { realDatetime } = useServerTime();
  const { internalUser, isVacationManager } = useLateralidade(user);

  // Contexto trazido pelo FlowTabs na troca de fluxo (aba a abrir e fase em exibição).
  // Capturado na MONTAGEM: logo depois a página re-navega para a própria rota (efeito de
  // troca de ciclo), e isso zeraria o location.state.
  const [flowNavState] = useState<{ tab?: 'diretoria' | 'rede'; stage?: string }>(
    () => (location.state as { tab?: 'diretoria' | 'rede'; stage?: string } | null) ?? {},
  );

  // Abas diretoria×rede do fluxo Normal. A aba MAD navega para o outro fluxo (FlowTabs);
  // na volta, o MAD manda em qual aba abrir.
  const [activeTab, setActiveTab] = useState<'diretoria' | 'rede'>(tab ?? flowNavState.tab ?? 'diretoria');

  const [pageLoading, setPageLoading] = useState(false);
  const [gestor, setIsGestor] = useState(false);
  const [playerInfo, setPlayerInfo] = useState<any>();

  const [filteredData, setFilteredData] = useState<any[]>();
  const [tasksByFilter, setTasksByFilter] = useState<any[]>();
  const [tasksStatus, setTasksStatus] = useState<string[]>([]);
  const [selectedtasksStatus, setSelectedTasksStatus] = useState<string>();
  const [selectedFeedbackStatus, setSelectedFeedbackStatus] = useState<string>();
  const [searchText, setSearchText] = useState<string>();
  const [showConcurrentActionModal, setShowConcurrentActionModal] = useState<string | undefined>();
  // Realização com o modal de ajuste de indicador aberto. Guarda o `owner` junto porque
  // a lista de indicadores é a do DONO, não a do gestor que está com o modal aberto.
  const [indicatorTask, setIndicatorTask] = useState<{ id: string; owner?: string } | undefined>();

  // Ordem do seletor "Ordenar por" (RDN-49): só apresentação, aplicada sobre tasksByFilter na renderização.
  const [ordem, setOrdem] = useState<OrdemDaFila>('atual');

  const [showMore, setShowMore] = useState(false);
  const [calendarElementsIndexOf] = useState<number>(5);
  const [calendarNumberOfElements, setCalendarNumberOfElements] = useState(5);
  const [calendarShowMore, setCalendarShowMore] = useState(false);

  // PARIDADE: no monólito o bônus "Carta Reconhecer+" nunca é carregado — getBonus/
  // verifyBonus só existem em código comentado (index.tsx:2142 e 5198). Chamar
  // verifyBonus aqui MUDARIA comportamento (TaskItem bonus e o remove_action do reset
  // passariam a disparar), então `bonus` permanece indefinido como lá.
  const [bonus] = useState<any>(undefined);

  const { data: rawData, loading, setLoading, reload } = useTasksData(getTasksManagerNormal);

  // Nível efetivo p/ calendário — port de configCycleAndStage (index.tsx:4177-4186),
  // que usa userCodEtapa(playerInfo, true).
  const codEtapaEfetivo = useMemo(() => {
    if (!playerInfo) return undefined;

    return userCodEtapa(buildNormalContext({ playerInfo, isVacationManager }), playerInfo, true);
  }, [isVacationManager, playerInfo]);

  const {
    stages,
    activeStageObj,
    getActiveEvaluationStep,
    currentCycle,
    setCurrentCycle,
    currentStage,
    setCurrentStage,
    optionsCycle,
    activeCycleSelected,
    setActiveCycleSelected,
    elementsIndexOf,
    numberOfElements,
    setNumberOfElements,
    configCycleAndStage,
  } = useCycleStage({
    cyclesExtended: cyclesExtended as any,
    cycleSelected,
    realDatetime,
    codEtapaEfetivo,
    requestedStage: flowNavState.stage,
  });

  // Elegibilidade Normal×MAD do gestor exibido NO CICLO SELECIONADO — alimenta as abas
  // (ELE-04) e tira o gestor daqui se o ciclo escolhido não tiver universo Diretoria/Rede.
  const eligibility = useFlowEligibility({
    playerId: internalUser?._id,
    cycleId: cycleSelected?._id,
    tasks: rawData,
    flow: 'diretoria-rede',
    currentStage,
  });

  // Com as abas de volta para dentro do card (e não mais no submenu do header), o card
  // precisa existir sempre que houver fluxo para trocar — senão o gestor elegível aos
  // dois ficaria preso neste fluxo fora da janela de apreciação.
  const canSwitchFlow = !!eligibility?.normal && !!eligibility?.mad;

  // Contexto-base das regras (sem cotas) — espelha os estados homônimos do monólito.
  const baseCtx: NormalRuleContext = useMemo(
    () =>
      buildNormalContext({
        playerInfo,
        userInfo: userInfo as any,
        internalUser: internalUser as any,
        realDatetime,
        activeStageObj: activeStageObj as any,
        getActiveEvaluationStep,
        cyclesExtended: cyclesExtended as any,
        realCycle: realCycle as any,
        isVacationManager,
        bonus,
      }),
    [
      activeStageObj,
      bonus,
      cyclesExtended,
      getActiveEvaluationStep,
      internalUser,
      isVacationManager,
      playerInfo,
      realCycle,
      realDatetime,
      userInfo,
    ],
  );

  // Enriquecimento com labels de status (UI-08) — no monólito feito dentro do loadData
  // (index.tsx:2109-2114); aqui derivado para manter o kernel useTasksData puro.
  const data = useMemo(() => {
    if (!Array.isArray(rawData)) return rawData;

    return rawData.map((task: any) => ({
      ...task,
      statuses: getTaskStatuses(baseCtx, task) || [],
    }));
  }, [baseCtx, rawData]);

  // Port de getUserInfo (index.tsx:2058-2072): playerInfo do gestor exibido, com pares
  // (showPairs=true). Cacheado por sessão (playerInfoCache): a troca de aba remonta o
  // fluxo e a guarda de render não deve esperar rede de novo pelo mesmo gestor.
  const getUserInfo = useCallback(async () => {
    try {
      if (playerInfo && playerInfo.chave === internalUser?._id) return;
      if (!internalUser?._id) return;

      const playerInfoResult = await getPlayerInfoWithPairs(internalUser._id);

      if (playerInfoResult) {
        setIsGestor(playerInfoResult.is_manager);
        setPlayerInfo(playerInfoResult);
        setLoading(false);
      }
    } catch (e) {
      console.log(e);
    }
  }, [internalUser?._id, playerInfo, setLoading]);

  // Port do loadData (index.tsx:2085-2148) com os parâmetros da fase fechados — é o
  // `reload` injetado nas ações/guards (com taskId devolve a task fresca).
  const loadData = useCallback(
    async (taskId?: string) => {
      if (!!activeStageObj) {
        const hasTaskId = typeof taskId === 'string' && taskId.length > 0;

        if (!hasTaskId) {
          setTasksStatus([]);
          setSelectedFeedbackStatus(undefined);
          setSelectedTasksStatus(undefined);
          setSearchText(undefined);
        }

        return reload(
          {
            player: internalUser?.extra.id,
            start: activeStageObj.date_start,
            end: activeStageObj.date_finish,
            step: (activeStageObj as any).step,
          },
          taskId,
        );
      }

      return typeof taskId === 'string' && taskId.length > 0 ? null : [];
    },
    [activeStageObj, internalUser?.extra?.id, reload],
  );

  // Port do memo isDirectManager (index.tsx:139-147).
  const isDirectManager = useMemo(
    () =>
      isVacationManager &&
      userInfo &&
      playerInfo &&
      ((userInfo as any)?._id === playerInfo?.gestor ||
        (Array.isArray((userInfo as any)?.mad_dupla) &&
          (userInfo as any)?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor))),
    [isVacationManager, playerInfo, userInfo],
  );

  // Port de index.tsx:699-708.
  const isOriginalManager = useMemo(() => {
    if (!filteredData?.length) return false;
    return !!filteredData.find(
      x =>
        x.original_manager &&
        (x.original_manager === playerInfo?._id ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(x.original_manager)))),
    );
  }, [filteredData, playerInfo]);

  // Port de index.tsx:710-721.
  const isOriginalManagerCod5 = useMemo(() => {
    if (!filteredData?.length || !playerInfo) return false;
    return !!filteredData.find(
      x =>
        x.original_manager &&
        (x.original_manager === playerInfo?._id ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(x.original_manager)))) &&
        userCodEtapa(baseCtx) &&
        userCodEtapa(baseCtx) === 5,
    );
  }, [baseCtx, filteredData, playerInfo]);

  // Port de index.tsx:723-728.
  const handleLinkSpaceKeyDown = useCallback((event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      event.currentTarget.click();
    }
  }, []);

  // Port do clarifyingText (index.tsx:753-933) — userCodEtapa SEM composição de dupla
  // (verifyMad false), como no original.
  const clarifyingText = useMemo(() => {
    if (
      !playerInfo ||
      !Array.isArray(filteredData) ||
      !userCodEtapa(baseCtx) ||
      typeof userCodEtapa(baseCtx) !== 'number' ||
      (userCodEtapa(baseCtx) as number) < 1 ||
      (userCodEtapa(baseCtx) as number) > 5
    )
      return null;

    const _tasks = [...(filteredData as any[])];
    let stepMsg;

    if (userCodEtapa(baseCtx) === 4 && !!isOriginalManager) {
      if (typeof getActiveEvaluationStep === 'number') {
        if (getActiveEvaluationStep === 2) {
          stepMsg = 0;
        } else if (getActiveEvaluationStep === 3) {
          stepMsg = 1;
        } else {
          stepMsg = 2;
        }
      } else {
        stepMsg = 2;
      }
    } else if (userCodEtapa(baseCtx) === 5 && !!isOriginalManager) {
      if (typeof getActiveEvaluationStep === 'number') {
        if (getActiveEvaluationStep === 2) {
          stepMsg = 0;
        } else if (getActiveEvaluationStep === 3) {
          stepMsg = 1;
        } else if (getActiveEvaluationStep === 4 || getActiveEvaluationStep === 4.1) {
          stepMsg = 2;
        } else {
          stepMsg = 3;
        }
      } else {
        stepMsg = 3;
      }
    } else {
      if (userCodEtapa(baseCtx) === 2) {
        stepMsg = 0;
      } else if (userCodEtapa(baseCtx) === 4) {
        stepMsg = 2;
      } else if (userCodEtapa(baseCtx) === 5) {
        stepMsg = 3;
      } else if (userCodEtapa(baseCtx) === 1 || userCodEtapa(baseCtx) === 3) {
        if (_tasks.length) {
          let hasLevel1 = false;

          _tasks.forEach(task => {
            if (
              typeof getTaskStepLocal(task) === 'number' &&
              (getTaskStepLocal(task) as number) >= 3 &&
              ((typeof getActiveEvaluationStep === 'number' && getActiveEvaluationStep > 2) || getActiveEvaluationStep === null)
            ) {
              hasLevel1 = true;
            }
          });

          stepMsg = hasLevel1 ? 1 : 0;
        } else {
          stepMsg = 0;
        }
      }
    }

    if (typeof stepMsg === 'number') {
      if (stepMsg === 1) {
        return (
          <>
            <p className="m-0 mb-1.5 font-bold text-brand-blue-dark">
              Apreciação das realizações validadas
            </p>
            <p className="m-0 mb-1">
              Cada realização recebida por você foi <strong className="font-bold">validada</strong> por funcionário pertencente à
              função gerencial e recebeu 300 (trezentos) pontos.
            </p>
            <p className="m-0 mb-1">
              Nesta apreciação, você poderá BONIFICAR todas as realizações que considerar mais relevantes em seu âmbito de
              atuação, e PROMOVER ao próximo nível, até 10% (dez por cento) das realizações bonificadas que você considerar
              extraordinárias.
            </p>
            <p className="m-0 mb-1">
              A realização <strong className="font-bold">bonificada</strong> passará a 600 (seiscentos) pontos e a{' '}
              <strong className="font-bold">promovida</strong> terá 100 (cem) pontos adicionais.
            </p>

            <p className="m-0">
              <b className="text-brand-blue-dark">Lembre-se</b> que ao deixar feedback para{' '}
              <strong className="font-bold">as entregas</strong> recebidas você promove o engajamento e o desenvolvimento de sua
              equipe.
            </p>
          </>
        );
      } else if (stepMsg === 2) {
        return (
          <>
            <p className="m-0 mb-1.5 font-bold text-brand-blue-dark">
              Apreciação das entregas promovidas pelo nível 1
            </p>
            <p className="m-0 mb-1">
              Cada realização recebida por você foi <b>promovida</b> por funcionário pertencente à função gerencial e recebeu 700 (setecentos) pontos.
            </p>
            <p className="m-0 mb-1">
              Nesta apreciação, você poderá BONIFICAR todas as realizações que considerar relevantes em seu âmbito de atuação, e PROMOVER ao próximo nível, até 20% (vinte por cento) das realizações bonificadas que você considerar extraordinárias.
            </p>
            <p className="m-0 mb-1">
              A realização <strong className="font-bold">bonificada</strong> passará a 1200 (mil e duzentos) pontos e a <strong className="font-bold">promovida</strong> terá 100 (cem) pontos adicionais.
            </p>

            <p className="m-0">
            Lembre-se que ao deixar feedback para as entregas recebidas você promove o engajamento e o desenvolvimento de sua equipe.
            </p>
          </>
        );
      } else if (stepMsg === 3) {
        return (
          <>
            <p className="m-0 mb-1.5 font-bold text-brand-blue-dark">
              Apreciação das entregas promovidas pelo nível 2
            </p>
            <p className="m-0 mb-1">
              Cada realização recebida por você foi promovida por funcionário responsável pela apreciação nível 2 e recebeu 1.300
              (mil e trezentos) pontos.
            </p>
            <p className="m-0 mb-1">
              Nesta apreciação, você poderá BONIFICAR VALEU para 100% das realizações, BONIFICAR MANDOU BEM para até 50%
              (cinquenta por cento) das realizações e BONIFICAR UAU para até 25% (vinte e cinco por cento) das realizações que
              você considerar extraordinárias.
            </p>
            <p className="m-0 mb-1">
              A realização <strong className="font-bold">Bonificada Valeu</strong> passará a 1.500 (mil e quinhentos) pontos, a
              realização <strong className="font-bold">Bonificada Mandou Bem</strong> passará a 2.500 (dois mil e quinhentos)
              pontos e a realização <strong className="font-bold">Bonificada UAU</strong> passará a 5.000 (cinco mil) pontos.
            </p>

            <p className="m-0">
              E que tal deixar um feedback para a equipe? Afinal, reconhecimento nunca é demais!
            </p>
          </>
        );
      } else {
        return (
          <>
            <p className="m-0 mb-1.5 font-bold text-brand-blue-dark">
              VALIDAÇÃO das entregas cadastradas por sua equipe
            </p>
            <p className="m-0 mb-1">
              Cada realização recebida por você foi cadastrada por um de seus liderados.
            </p>
            <p className="m-0 mb-1">
              Para VALIDAR a entrega, verifique se há alinhamento com os indicadores e desafios da equipe, se não há informações
              sigilosas, duplicidade e ou conteúdo inadequado, caso contrário você pode DEVOLVER a realização.
            </p>
            <p className="m-0 mb-1">
              Ao ser devolvida, a realização volta para o responsável pelo cadastramento e se for corrigida, dentro do período de
              validação, você poderá analisá-la novamente.
            </p>
            <p className="m-0 mb-1">
              A realização <strong className="font-bold">validada</strong> recebe 300 (trezentos) pontos.
            </p>

            <p className="m-0">
              <b className="text-brand-blue-dark">Lembre-se</b> que ao deixar feedback para{' '}
              <strong className="font-bold">as entregas</strong> recebidas você promove o engajamento e o desenvolvimento de sua
              equipe.
            </p>
          </>
        );
      }
    } else {
      return null;
    }
  }, [baseCtx, filteredData, getActiveEvaluationStep, isOriginalManager, playerInfo]);

  // Port de index.tsx:948-960.
  const getFullCalendar = useMemo(() => {
    if (!cyclesExtended || !cycleSelected) return null;

    const cycleActive = cyclesExtended.find(x => x._id === cycleSelected?._id);

    if (cycleActive) {
      const calendar = getFutureCalendar(cycleActive?.steps, realDatetime);
      return calendar;
    }

    return null;
  }, [cycleSelected, cyclesExtended, realDatetime]);

  // Port de hasRedeMAD (index.tsx:3156-3178). No aggregate Normal não vêm tasks MAD,
  // mas a flag é preservada porque decide a exibição das tabs e o sub-filtro diretoria.
  const hasRedeMAD: { rede: boolean; mad: boolean } | undefined = useMemo(() => {
    if (!Array.isArray(data) || !data.length || !playerInfo || !activeStageObj) return undefined;

    const obj = { rede: false, mad: false };

    const itemsByStage = data?.filter(item => {
      return item.created >= (activeStageObj as any)?.date_start && item.created <= (activeStageObj as any)?.date_finish;
    });

    if (itemsByStage.length) {
      if (
        userCodEtapa(baseCtx) &&
        userCodEtapa(baseCtx) === 5 &&
        (activeStageObj as any)?.type &&
        (activeStageObj as any)?.type === 'semester'
      ) {
        obj.rede = !!itemsByStage.filter(
          (x: { prosseguir_gst: string; owner_tipo_classificacao: string }) =>
            (!!x.prosseguir_gst && x.prosseguir_gst === 'approved') ||
            (!!x.owner_tipo_classificacao && x.owner_tipo_classificacao === 'UNIDADE TÁTICA'),
        ).length;
      }

      obj.mad = !!itemsByStage.filter(x => !!x.mad || (Array.isArray(x.non_evaluator) && x.non_evaluator.length > 0)).length;
    }

    return obj;
  }, [activeStageObj, baseCtx, data, playerInfo]);

  // Port de tasksProsseguirAssessor (index.tsx:3142-3154).
  const tasksProsseguirAssessor: any[] = useMemo((): any[] => {
    if (!Array.isArray(filteredData) || !filteredData.length || !playerInfo) return [];

    const tasks = filteredData.filter(task => {
      if (!!task.prosseguir_idGestor && task.prosseguir_idGestor === playerInfo._id) return true;

      if (Array.isArray(task.prosseguir_managers) && !!task.prosseguir_managers.includes(playerInfo._id)) return true;

      return false;
    });

    return tasks;
  }, [filteredData, playerInfo]);

  // Universo por fase + tab (port de index.tsx:3451-3570, SEM o ramo mad das tabs):
  // filterStageData cobre COT-01; aqui entram só os sub-filtros diretoria/rede.
  useEffect(() => {
    function updateData() {
      if (!hasRedeMAD) return setFilteredData([]);

      try {
        let stageData: any[] = filterStageData(baseCtx, data);

        if (hasRedeMAD.mad || hasRedeMAD.rede) {
          const isMadOrNonEvaluator = (x: any) => !!x.mad;

          if (activeTab === 'rede') {
            stageData = stageData.filter(
              (x: any) =>
                (x.prosseguir_gst === 'approved' || x.owner_tipo_classificacao === 'UNIDADE TÁTICA') && !isMadOrNonEvaluator(x),
            );
          } else {
            stageData = hasRedeMAD.rede
              ? stageData.filter(
                  (x: any) => !x.prosseguir_gst && !isMadOrNonEvaluator(x) && x.owner_tipo_classificacao !== 'UNIDADE TÁTICA',
                )
              : stageData.filter((x: any) => !isMadOrNonEvaluator(x));
          }
        }

        const statuses: string[] = ['Selecione'];

        stageData.forEach((task: any) => {
          if (Array.isArray(task.statuses) && task.statuses.length) {
            task.statuses.forEach((_status: string) => {
              if (!statuses.find(x => x === _status)) {
                statuses.push(_status);
              }
            });
          }
        });

        setTasksStatus(statuses);

        setFilteredData(stageData);

        setLoading(false);
      } catch (e) {
        setFilteredData([]);
        setLoading(false);
      }
    }

    if (playerInfo) updateData();
  }, [activeTab, baseCtx, data, hasRedeMAD, playerInfo, setLoading]);

  // Cotas e universos da fase (COT-04..09) — no monólito viviam no mesmo effect acima;
  // aqui derivadas do universo filtrado.
  const quotas = useMemo(() => computeQuotas(baseCtx, Array.isArray(filteredData) ? filteredData : []), [baseCtx, filteredData]);

  // Contexto COMPLETO das regras: base + cotas (equivale aos estados doubled/promoted/
  // uau/mandouBem do monólito depois do updateData).
  const ctx: NormalRuleContext = useMemo(
    () => ({
      ...baseCtx,
      doubledTasks: quotas.doubledTasks,
      maxDoubledTasks: quotas.maxDoubledTasks,
      promotedTasks: quotas.promotedTasks,
      maxPromotedTasks: quotas.maxPromotedTasks,
      uauTasks: quotas.uauTasks,
      maxUauTasks: quotas.maxUauTasks,
      mandouBemTasks: quotas.mandouBemTasks,
      maxMandouBemTasks: quotas.maxMandouBemTasks,
    }),
    [baseCtx, quotas],
  );

  const {
    selectedGestor,
    setSelectedGestor,
    functionGestor,
    getAction,
    setGetAction,
    modalCod5,
    setModalCod5,
    handleAccept,
    handleStop,
    handleReject,
    handleDoubled,
    handlePromote,
    handleAward,
    handleReset,
    handleSubmitCod5,
  } = useNormalActions({
    ctx,
    data,
    filteredData,
    reload: loadData,
    setPageLoading,
    showConcurrentModal: setShowConcurrentActionModal,
    quotas,
    bonus,
    user,
    isVacationManager,
  });

  const functionsObject: any = {
    handleAccept,
    handlePromote,
  };

  // PARIDADE: port do htmlCountdownStep (index.tsx:3180-3432) — no monólito o memo é
  // computado mas NUNCA renderizado (nenhuma referência no JSX); preservamos o código
  // (inclusive o ramo do "buraco" para om cod4/5 via getTaskMonthStep2Start) sem
  // renderizá-lo, para manter comportamento idêntico.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const htmlCountdownStep = useMemo(() => {
    if (!activeStageObj || !playerInfo || (activeStageObj as any).status === 'closed' || !realDatetime) return <></>;

    const itemsByStage: any[] = data?.length ? [...data] : [];
    const levelTitle = getActiveEvaluationStep
      ? getActiveEvaluationStep === 4.1
        ? 'Prosseguir'
        : getActiveEvaluationStep === 2
          ? 'Validação'
          : getActiveEvaluationStep === 3
            ? 'Nível 1'
            : getActiveEvaluationStep === 4
              ? 'Nível 2'
              : getActiveEvaluationStep === 5
                ? 'Nível 3'
                : null
      : null;

    if (itemsByStage.length) {
      const userTaskStep: number[] = itemsByStage?.map(task => getPlayerStep(baseCtx, task) as number).sort(() => -1);

      if (getActiveEvaluationStep && userTaskStep.includes(getActiveEvaluationStep)) {
        const date_finish =
          getActiveEvaluationStep === 2
            ? (activeStageObj as any)?.date_evaluation_step2_finish
            : getActiveEvaluationStep === 3
              ? (activeStageObj as any)?.date_evaluation_step3_finish
              : getActiveEvaluationStep === 4
                ? (activeStageObj as any)?.date_evaluation_step4_finish
                : getActiveEvaluationStep === 4.1
                  ? (activeStageObj as any)?.date_evaluation_pitStop_finish
                  : (activeStageObj as any)?.date_evaluation_step5_finish;

        return (
          <div className="py-4">
            {!!levelTitle && (
              <p tabIndex={0} className="text-xs text-alice-blue block font-thin">
                Fluxo de apreciação atual:{' '}
                <strong className="text-xs text-alice-blue block font-normal whitespace-nowrap">{levelTitle}</strong>
              </p>
            )}
            <p tabIndex={0} className="text-xs text-alice-blue block font-thin">
              O período de apreciação deste nível termina em:{' '}
              <strong className="text-xs text-alice-blue block font-normal whitespace-nowrap">
                <Countdown key="missao" deadline={new Date(date_finish)} />
              </strong>
            </p>
          </div>
        );
      } else {
        if (getActiveEvaluationStep) {
          let hasAfter = userTaskStep?.find((x: number) => x > (getActiveEvaluationStep as number));

          const date_start =
            hasAfter === 2
              ? (activeStageObj as any)?.date_evaluation_step2_start
              : hasAfter === 3
                ? (activeStageObj as any)?.date_evaluation_step3_start
                : hasAfter === 4
                  ? (activeStageObj as any)?.date_evaluation_step4_start
                  : hasAfter === 4.1
                    ? (activeStageObj as any)?.date_evaluation_pitStop_start
                    : (activeStageObj as any)?.date_evaluation_step5_start;

          if (!!hasAfter && date_start && realDatetime < date_start) {
            return (
              <div className="py-4">
                <p tabIndex={0} className="text-xs text-alice-blue block font-thin">
                  O período de apreciação começa em:{' '}
                  <strong className="text-xs text-alice-blue block font-normal whitespace-nowrap">
                    <Countdown key="missao" deadline={new Date(date_start)} />
                  </strong>
                </p>
              </div>
            );
          } else {
            return <></>;
          }
        } else {
          // Diretor (5) / Nível 2 (4) que é gestor original direto: a apreciação de cada
          // realização começa no step2_start do MÊS dela (não no da fase semestral/trimestral).
          // Só faz sentido mostrar "começa em" quando houver realização cujo mês ainda não
          // abriu; se ele já pode apreciar algo agora, não exibimos contagem.
          const codEtapaNow = userCodEtapa(baseCtx);
          if ((codEtapaNow === 4 || codEtapaNow === 5) && isOriginalManager) {
            const pendingStarts = itemsByStage
              .filter(
                (item: any) =>
                  String(item.original_manager) === String(playerInfo?._id) ||
                  (Array.isArray(playerInfo?.mad_dupla) &&
                    playerInfo?.mad_dupla?.some((md: any) => md._id && md._id === String(item.original_manager))),
              )
              .map((item: any) => getTaskMonthStep2Start(baseCtx, item))
              .filter((d: any): d is number => typeof d === 'number' && realDatetime < d);

            const nextStart = pendingStarts.length ? Math.min(...pendingStarts) : null;

            if (nextStart) {
              return (
                <div className="py-4">
                  <p tabIndex={0} className="text-xs text-alice-blue block font-thin">
                    O período de apreciação começa em:{' '}
                    <strong className="text-xs text-alice-blue block font-normal whitespace-nowrap">
                      <Countdown key="missao" deadline={new Date(nextStart)} />
                    </strong>
                  </p>
                </div>
              );
            }

            return <></>;
          }

          const date_start =
            realDatetime < (activeStageObj as any)?.date_evaluation_step2_start
              ? (activeStageObj as any)?.date_evaluation_step2_start
              : realDatetime < (activeStageObj as any)?.date_evaluation_step3_start
                ? (activeStageObj as any)?.date_evaluation_step3_start
                : realDatetime < (activeStageObj as any)?.date_evaluation_step4_start
                  ? (activeStageObj as any)?.date_evaluation_step4_start
                  : realDatetime < (activeStageObj as any)?.date_evaluation_pitStop_start
                    ? (activeStageObj as any)?.date_evaluation_pitStop_start
                    : realDatetime < (activeStageObj as any)?.date_evaluation_step5_start
                      ? (activeStageObj as any)?.date_evaluation_step5_start
                      : null;

          if (date_start && realDatetime < date_start) {
            return (
              <div className="py-4">
                <p tabIndex={0} className="text-xs text-alice-blue block font-thin">
                  O período de apreciação começa em:{' '}
                  <strong className="text-xs text-alice-blue block font-normal whitespace-nowrap">
                    <Countdown key="missao" deadline={new Date(date_start)} />
                  </strong>
                </p>
              </div>
            );
          } else {
            return <></>;
          }
        }
      }
    } else {
      // No monólito: userCodEtapa(null, activeTabDirector === 'mad') — no fluxo Normal
      // a tab mad não existe, então o verifyMad é sempre false.
      const codEtapa =
        userCodEtapa(baseCtx) === 2
          ? 2
          : userCodEtapa(baseCtx) === 1 || userCodEtapa(baseCtx) === 3
            ? 3
            : userCodEtapa(baseCtx) === 4
              ? 4
              : userCodEtapa(baseCtx) === 5
                ? 5
                : null;

      if (codEtapa) {
        if (getActiveEvaluationStep && getActiveEvaluationStep === codEtapa) {
          const date_finish =
            getActiveEvaluationStep === 2
              ? (activeStageObj as any)?.date_evaluation_step2_finish
              : getActiveEvaluationStep === 3
                ? (activeStageObj as any)?.date_evaluation_step3_finish
                : getActiveEvaluationStep === 4
                  ? (activeStageObj as any)?.date_evaluation_step4_finish
                  : (activeStageObj as any)?.date_evaluation_step5_finish;

          return (
            <div className="py-4">
              {!!levelTitle && (
                <p tabIndex={0} className="text-xs text-alice-blue block font-thin">
                  Fluxo de apreciação atual:{' '}
                  <strong className="text-xs text-alice-blue block font-normal whitespace-nowrap">{levelTitle}</strong>
                </p>
              )}
              <p tabIndex={0} className="text-xs text-alice-blue block font-thin">
                O período de apreciação deste nível termina em:{' '}
                <strong className="text-xs text-alice-blue block font-normal whitespace-nowrap">
                  <Countdown key="missao" deadline={new Date(date_finish)} />
                </strong>
              </p>
            </div>
          );
        } else {
          if (getActiveEvaluationStep && getActiveEvaluationStep < codEtapa) {
            const date_start =
              codEtapa === 2
                ? (activeStageObj as any)?.date_evaluation_step2_start
                : codEtapa === 3
                  ? (activeStageObj as any)?.date_evaluation_step3_start
                  : codEtapa === 4
                    ? (activeStageObj as any)?.date_evaluation_step4_start
                    : (activeStageObj as any)?.date_evaluation_step5_start;

            return (
              <div className="py-4">
                <p tabIndex={0} className="text-xs text-alice-blue block font-thin">
                  O período de apreciação começa em
                  {!!date_start ? (
                    <>
                      :{' '}
                      <strong className="text-xs text-alice-blue block font-normal whitespace-nowrap">
                        <Countdown key="missao" deadline={new Date(date_start)} />
                      </strong>
                    </>
                  ) : (
                    ' breve.'
                  )}
                </p>
              </div>
            );
          } else {
            const date_start =
              realDatetime < (activeStageObj as any)?.date_evaluation_step2_start && codEtapa === 2
                ? (activeStageObj as any)?.date_evaluation_step2_start
                : realDatetime < (activeStageObj as any)?.date_evaluation_step3_start && codEtapa === 3
                  ? (activeStageObj as any)?.date_evaluation_step3_start
                  : realDatetime < (activeStageObj as any)?.date_evaluation_step4_start && codEtapa === 4
                    ? (activeStageObj as any)?.date_evaluation_step4_start
                    : realDatetime < (activeStageObj as any)?.date_evaluation_pitStop_start && codEtapa === 5
                      ? (activeStageObj as any)?.date_evaluation_pitStop_start
                      : null;

            if (date_start && realDatetime < date_start) {
              return (
                <div className="py-4">
                  <p tabIndex={0} className="text-xs text-alice-blue block font-thin">
                    O período de apreciação começa em:{' '}
                    <strong className="text-xs text-alice-blue block font-normal whitespace-nowrap">
                      <Countdown key="missao" deadline={new Date(date_start)} />
                    </strong>
                  </p>
                </div>
              );
            } else {
              return <></>;
            }
          }
        }
      } else {
        return <></>;
      }
    }
  }, [activeStageObj, baseCtx, data, getActiveEvaluationStep, isOriginalManager, playerInfo, realDatetime]);

  // Filtro por feedback/status/texto (port de index.tsx:3966-3998, UI-05).
  useEffect(() => {
    if (Array.isArray(filteredData) && filteredData.length) {
      let _data = Array.isArray(filteredData) && filteredData.length ? filteredData : [];

      if (
        typeof selectedFeedbackStatus === 'string' &&
        selectedFeedbackStatus.length &&
        selectedFeedbackStatus.toLowerCase() !== 'selecione'
      ) {
        _data = _data.filter((task: any) => {
          const hasFeedback = hasTaskFeedbackForUser(baseCtx, task);
          return selectedFeedbackStatus === 'sim' ? hasFeedback : !hasFeedback;
        });
      }

      if (
        typeof selectedtasksStatus === 'string' &&
        selectedtasksStatus.length &&
        selectedtasksStatus.toLowerCase() !== 'selecione'
      ) {
        _data = _data.filter(x => !!x.statuses.includes(selectedtasksStatus));
      }

      if (typeof searchText === 'string' && searchText.trim().length) {
        const _search = searchText.replace(/ +/gi, ' ').trim().toLowerCase();
        _data = _data.filter(x => matchesSearch(x, _search));
      }

      setTasksByFilter(_data);
    } else {
      setTasksByFilter([]);
    }
  }, [baseCtx, filteredData, searchText, selectedFeedbackStatus, selectedtasksStatus]);

  const feedbackFilterOptions = useMemo(() => {
    const tasks = Array.isArray(filteredData) ? filteredData : [];
    const withFeedback = tasks.filter(task => hasTaskFeedbackForUser(baseCtx, task)).length;
    const withoutFeedback = tasks.length - withFeedback;

    return [
      {
        label: 'Selecione',
        value: 'selecione',
      },
      {
        label: `Com feedback (${withFeedback})`,
        value: 'sim',
      },
      {
        label: `Sem feedback (${withoutFeedback})`,
        value: 'nao',
      },
    ];
  }, [baseCtx, filteredData]);

  const selectedFeedbackOption = useMemo(
    () => feedbackFilterOptions.find(option => option.value === selectedFeedbackStatus) || null,
    [feedbackFilterOptions, selectedFeedbackStatus],
  );

  const taskStatusOptions = useMemo(() => {
    const tasks = Array.isArray(filteredData) ? filteredData : [];

    return (
      tasksStatus?.map(status => ({
        label: `${status}${status !== 'Selecione' ? ` (${tasks.filter(x => !!x.statuses.includes(status)).length})` : ''}`,
        value: status,
      })) || []
    );
  }, [filteredData, tasksStatus]);

  const selectedTaskStatusOption = useMemo(
    () => taskStatusOptions.find(option => option.value === selectedtasksStatus) || null,
    [selectedtasksStatus, taskStatusOptions],
  );

  // Port de index.tsx:4042-4047.
  useEffect(() => {
    if (!internalUser && !playerInfo) return;

    setLoading(true);
    getUserInfo();
  }, [getUserInfo, internalUser, playerInfo, setLoading]);

  // Port de index.tsx:4049-4055.
  useEffect(() => {
    if (!gestor) return;

    setLoading(true);

    loadData();
  }, [gestor, loadData, setLoading]);

  // Port de index.tsx:4057-4065.
  useEffect(() => {
    if (!stages) return;

    if (stages.length <= numberOfElements) {
      setShowMore(false);
    } else {
      setShowMore(true);
    }
  }, [numberOfElements, stages]);

  // Port de index.tsx:4067-4075.
  useEffect(() => {
    if (!getFullCalendar) return;

    if (getFullCalendar.length <= calendarNumberOfElements) {
      setCalendarShowMore(false);
    } else {
      setCalendarShowMore(true);
    }
  }, [calendarNumberOfElements, getFullCalendar]);

  // Port de index.tsx:4077-4091 — recarrega ao voltar da tela de detalhe.
  useEffect(() => {
    try {
      const locationState: any = location.state;

      if (locationState && locationState.direction) {
        if (locationState.direction === 'back') {
          if (gestor) {
            loadData();
          }
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, [gestor, loadData, location]);

  // O setLoading(true) do evento reload:lateralidade era feito no monólito
  // (index.tsx:4118-4122); o useLateralidade do kernel delega essa parte à página.
  useEffect(() => {
    function handleLateralidadeLoading() {
      setLoading(true);
    }

    document.addEventListener('reload:lateralidade', handleLateralidadeLoading, false);

    return () => {
      document.removeEventListener('reload:lateralidade', handleLateralidadeLoading);
    };
  }, [setLoading]);

  // Port de index.tsx:4207-4211 (o kernel exige internalUser pronto — mesma guarda do
  // configCycleAndStage original).
  useEffect(() => {
    if (!user || !cyclesExtended || !internalUser) return;

    configCycleAndStage();
  }, [configCycleAndStage, cyclesExtended, internalUser, user]);

  // Estar AQUI é a escolha: grava a visita para o guarda-chuva voltar a diretoria-rede
  // nesta sessão (ELE-02) — não só o clique no switcher conta.
  useEffect(() => {
    setFlowChoice('diretoria-rede');
  }, []);

  function getIndexByValue(array: any, value: string) {
    return array.findIndex((element: any) => element.value === value);
  }

  // Guardas de render — port de index.tsx:4239-4277. Em vez de tela em branco (null),
  // renderiza o shell da página com spinner enquanto user/ciclo/fase resolvem.
  if (
    !user ||
    !internalUser ||
    typeof gestor !== 'boolean' ||
    typeof currentCycle === 'undefined' ||
    typeof currentStage === 'undefined' ||
    !activeStageObj
  ) {
    return <FlowLoading />;
  }

  if (!gestor && !loading) {
    return (
      <div className="mx-auto container p-4 lg:p-10">
        <div className="flex flex-row items-center space-x-3 mb-16">
          <div>
            <Link to="../" tabIndex={-1}>
              <button className="rounded-full bg-purple flex justify-center items-center p-1">
                <RiArrowLeftSLine size={28} className="text-white" />
              </button>
            </Link>
          </div>
          <h1 tabIndex={0} className="text-lg lg:text-3xl py-1 rounded-full">
            Minha Equipe
          </h1>
        </div>

        <div className="border-2 border-electric-blue rounded-3xl p-8 bg-oxford-blue">
          <p tabIndex={0} className="text-dandelion text-lg font-semibold block text-center">
            Você não possui acesso à esta página.
          </p>
        </div>
      </div>
    );
  }

  if (user?.extra.rede) return <NotFound />;

  // Expressões que o JSX antigo avaliava inline, extraídas sem mudança para o JSX novo.
  const faseSemRealizacoes = (activeStageObj as any)?._id === '68af4da8458e527eaa788503';

  const mostraCartaoDaFase =
    (activeStageObj as any)?.status === 'open' ||
    (getActiveEvaluationStep &&
      ((getActiveEvaluationStep === 5 && (activeStageObj as any)?.type && (activeStageObj as any)?.type === 'semester') ||
        (getActiveEvaluationStep >= 4 &&
          getActiveEvaluationStep < 5 &&
          (activeStageObj as any)?.type &&
          (activeStageObj as any)?.type === 'trimester') ||
        (getActiveEvaluationStep < 4 && (activeStageObj as any)?.type && (activeStageObj as any)?.type === 'month'))) ||
    (isDirectManager &&
      (userCodEtapa(baseCtx, userInfo) === 1 ||
        userCodEtapa(baseCtx, userInfo) === 3 ||
        userCodEtapa(baseCtx, userInfo) === 4 ||
        userCodEtapa(baseCtx, userInfo) === 5));

  const mostraAbasForaDaJanela = !!hasRedeMAD?.mad || !!hasRedeMAD?.rede || canSwitchFlow;

  const mostraContadores =
    !!(
      getActiveEvaluationStep ||
      (isDirectManager &&
        (userCodEtapa(baseCtx, userInfo) === 1 ||
          userCodEtapa(baseCtx, userInfo) === 3 ||
          userCodEtapa(baseCtx, userInfo) === 4 ||
          userCodEtapa(baseCtx, userInfo) === 5))
    ) &&
    !!(
      getActiveEvaluationStep &&
      ((getActiveEvaluationStep === 5 &&
        userCodEtapa(baseCtx, userInfo) === 5 &&
        (activeStageObj as any)?.type &&
        (activeStageObj as any)?.type === 'semester') ||
        ((getActiveEvaluationStep === 4 || getActiveEvaluationStep === 4.1) &&
          (userCodEtapa(baseCtx, userInfo) === 4 || userCodEtapa(baseCtx, userInfo) === 5) &&
          (activeStageObj as any)?.type &&
          (activeStageObj as any)?.type === 'trimester') ||
        (getActiveEvaluationStep < 4 &&
          (userCodEtapa(baseCtx, userInfo) as number) < 4 &&
          (activeStageObj as any)?.type &&
          (activeStageObj as any)?.type === 'month') ||
        (isDirectManager &&
          (userCodEtapa(baseCtx, userInfo) === 1 ||
            userCodEtapa(baseCtx, userInfo) === 3 ||
            userCodEtapa(baseCtx, userInfo) === 4 ||
            userCodEtapa(baseCtx, userInfo) === 5)))
    );

  const faseComJanela = !loading && !faseSemRealizacoes;

  const filtroDeFeedbackAplicado =
    typeof selectedFeedbackStatus === 'string' &&
    selectedFeedbackStatus.length &&
    selectedFeedbackStatus.toLowerCase() !== 'selecione';

  const filtroDeStatusAplicado =
    typeof selectedtasksStatus === 'string' && selectedtasksStatus.length && selectedtasksStatus.toLowerCase() !== 'selecione';

  // Regra do vazio por filtro da página antiga (o filtro de status não entra nela).
  const vazioPorFiltro = !!(
    Array.isArray(filteredData) &&
    filteredData.length &&
    (searchText?.length || filtroDeFeedbackAplicado)
  );

  function trocarDeCiclo(valor: string) {
    setActiveCycleSelected(valor);
    setCurrentCycle(undefined);
    setCurrentStage(undefined);

    const selected = cyclesExtended?.find(x => x._id === valor);

    if (selected) {
      setCycleSelected({
        _id: selected._id,
        title: selected.title,
        version: GetVersion(selected.date_start),
      });
    }
  }

  function limparFiltros() {
    setSearchText(undefined);
    setSelectedFeedbackStatus(undefined);
    setSelectedTasksStatus(undefined);
  }

  // Cartão que corresponde a uma opção dos filtros atuais vira botão desse filtro (premissa "Indicadores da fila").
  function filtrarPorStatus(status: string): Partial<IndicadorDaFila> {
    if (!taskStatusOptions.some(option => option.value === status)) return {};
    const ativo = selectedtasksStatus === status;
    return { ativo, onFiltrar: () => setSelectedTasksStatus(ativo ? undefined : status) };
  }

  function filtrarPorFeedback(valor: string): Partial<IndicadorDaFila> {
    const ativo = selectedFeedbackStatus === valor;
    return { ativo, onFiltrar: () => setSelectedFeedbackStatus(ativo ? undefined : valor) };
  }

  // Contadores da página antiga, nas mesmas condições e com os mesmos valores de `quotas` (RDN-48).
  function indicadoresDaFase(): IndicadorDaFila[] {
    const indicadores: IndicadorDaFila[] = [];

    if (!!isOriginalManager) {
      if (!!quotas.maxValidTasks) {
        indicadores.push({
          chave: 'validadas',
          rotulo: 'Total de realizações validadas',
          valor: `${quotas.validTasks?.length || 0}/${quotas.maxValidTasks || 0}`,
          tom: 'verde',
        });
      }

      if (!!quotas.maxRejectedTasks) {
        indicadores.push({
          chave: 'devolvidas',
          rotulo: 'Total de realizações devolvidas',
          valor: `${quotas.rejectedTasks?.length || 0}/${quotas.maxRejectedTasks || 0}`,
          tom: 'rosa',
          ...filtrarPorStatus('Devolvida'),
        });
      }
    }

    if (
      !!isOriginalManagerCod5 ||
      (userCodEtapa(baseCtx) &&
        userCodEtapa(baseCtx) !== 2 &&
        (userCodEtapa(baseCtx) as number) < 5 &&
        getActiveEvaluationStep &&
        getActiveEvaluationStep > 2) ||
      (userCodEtapa(baseCtx) && userCodEtapa(baseCtx) !== 2 && (userCodEtapa(baseCtx) as number) < 5 && isDirectManager)
    ) {
      if (!!quotas.maxDoubledTasks) {
        indicadores.push({
          chave: 'bonificadas',
          rotulo: 'Total de realizações bonificadas',
          valor: `${quotas.doubledTasks?.length || 0}/${quotas.maxDoubledTasks || 0}`,
          tom: 'amarelo',
        });
      }

      if (userCodEtapa(baseCtx) !== 5 && !!quotas.maxPromotedTasks) {
        indicadores.push({
          chave: 'promovidas',
          rotulo:
            userCodEtapa(baseCtx) === 5 &&
            (getActiveEvaluationStep === 5 ||
              playerInfo.funcao_localizacao === 3001 ||
              playerInfo.funcao_localizacao === 3002 ||
              !isOriginalManager)
              ? 'Total de selos concedidos'
              : 'Total de realizações promovidas',
          valor: `${quotas.promotedTasks?.length || 0}/${quotas.maxPromotedTasks || 0}`,
          tom: 'azul',
        });
      }
    }

    if (tasksProsseguirAssessor.length) {
      const enviadas = tasksProsseguirAssessor.filter(item => {
        return (
          (item.prosseguir_who_approved && item.prosseguir_who_approved === internalUser?.extra.id) ||
          (Array.isArray(item.prosseguir_managers) &&
            item.prosseguir_managers.length >= 2 &&
            item.prosseguir_managers[0] === playerInfo._id)
        );
      }).length;

      indicadores.push({
        chave: 'enviadas-diretoria',
        rotulo: 'Total de realizações enviadas para apreciação da diretoria',
        valor: `${enviadas}/${tasksProsseguirAssessor.length}`,
        tom: 'ciano',
      });
    }

    if (userCodEtapa(baseCtx) === 5 && (activeStageObj as any)?.type && (activeStageObj as any)?.type === 'semester') {
      indicadores.push(
        {
          chave: 'valeu',
          rotulo: 'Total de realizações Valeu',
          valor: `${quotas.valeuTasks?.length || 0}/${quotas.maxValeuTasks || 0}`,
          tom: 'amarelo',
        },
        {
          chave: 'mandou-bem',
          rotulo: 'Total de realizações Mandou Bem',
          valor: `${quotas.mandouBemTasks?.length || 0}/${quotas.maxMandouBemTasks || 0}`,
          tom: 'amarelo',
        },
        {
          chave: 'uau',
          rotulo: 'Total de realizações Uau',
          valor: `${quotas.uauTasks?.length || 0}/${quotas.maxUauTasks || 0}`,
          tom: 'amarelo',
        },
      );
    }

    indicadores.push({
      chave: 'feedbacks',
      rotulo: 'Total de feedbacks concedidos',
      valor: `${quotas.feedbackTasks}/${quotas.maxValidTasks}`,
      tom: 'ciano',
      ...filtrarPorFeedback('sim'),
    });

    return indicadores;
  }

  const cicloEmExibicao = optionsCycle.find(x => x.value === cycleSelected?._id)
    ? optionsCycle.find(x => x.value === cycleSelected?._id)
    : optionsCycle[getIndexByValue(optionsCycle, activeCycleSelected)];

  return (
    <>
      {!!pageLoading && (
        <div className="fixed z-40 w-full h-full top-0 left-0 block flex items-center justify-center bg-black bg-opacity-60">
          <div role="status" className="flex items-center justify-center flex-col space-y-2 text-white">
            <svg
              aria-hidden="true"
              className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span tabIndex={0} className="text-white">
              Carregando...
            </span>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0" aria-busy={loading}>
        <TopoDoGestor
          pendentes={eligibility?.normalPending}
          fase={activeStageObj}
          etapaAtiva={getActiveEvaluationStep}
          inicioDaApreciacao={
            faseComJanela && mostraCartaoDaFase && activeStageObj && (activeStageObj as any).status === 'open'
              ? (activeStageObj as any).date_finish
              : undefined
          }
        />

        <SeletoresDaFila
          ciclos={optionsCycle}
          ciclo={cicloEmExibicao?.value ?? ''}
          onCiclo={trocarDeCiclo}
          fases={stages ?? []}
          fase={currentStage}
          onFase={setCurrentStage}
          desabilitado={loading}
          lateralidade={
            !!isVacationManager && !!internalUser
              ? {
                  verbo: userCodEtapa(baseCtx) && userCodEtapa(baseCtx) === 2 ? 'validando' : 'apreciando',
                  nome: internalUser.name,
                  detalhe: `${internalUser._id} · ${internalUser.extra?.dependencia} - ${internalUser.extra?.funcao}`,
                  onSairKeyDown: handleLinkSpaceKeyDown,
                }
              : undefined
          }
          listas={
            faseComJanela && (mostraCartaoDaFase || mostraAbasForaDaJanela) ? (
              <FlowTabs
                active={activeTab}
                eligibility={eligibility}
                hasRede={!!hasRedeMAD?.rede}
                currentStage={currentStage}
                onSelectTab={setActiveTab}
              />
            ) : undefined
          }
        />


        <SecaoDaFila
          rotuloDaFase={!loading && filteredData ? `Fase ${getMonthNameFromString((activeStageObj as any)?.title)}` : undefined}
          contagem={
            !loading && filteredData ? { naLista: tasksByFilter?.length ?? 0, noEscopo: filteredData.length } : undefined
          }
          exportar={
            tasksByFilter && tasksByFilter?.length > 0 ? (
              <ExportButton data={tasksByFilter} className={CLASSES_DO_EXPORTAR} />
            ) : undefined
          }
          indicadores={faseComJanela && mostraCartaoDaFase && mostraContadores ? indicadoresDaFase() : []}
          filtros={
            !loading && filteredData && filteredData?.length > 0
              ? {
                  busca: searchText || '',
                  onBusca: setSearchText,
                  mostrarSeletores: Array.isArray(tasksStatus) && !!tasksStatus.length,
                  opcoesDeFeedback: feedbackFilterOptions,
                  feedback: selectedFeedbackStatus,
                  onFeedback: setSelectedFeedbackStatus,
                  opcoesDeStatus: taskStatusOptions,
                  status: selectedtasksStatus,
                  onStatus: setSelectedTasksStatus,
                  ordem,
                  onOrdem: setOrdem,
                  filtrosAtivos: !!searchText?.length || !!filtroDeFeedbackAplicado || !!filtroDeStatusAplicado,
                  onLimparFiltros: limparFiltros,
                }
              : undefined
          }
          orientacoes={clarifyingText}
          pitStop={!loading && getActiveEvaluationStep === 4.1 ? periodoDa('Pit Stop', activeStageObj) ?? null : undefined}
        >
          {loading ? (
            <CartoesCarregando />
          ) : tasksByFilter?.length ? (
            ordenarFila(tasksByFilter, ordem).map(item => (
              <CartaoDaFila
                variante="normal"
                key={item._id}
                editable={canEdit(ctx, item)}
                bonus={
                  !!bonus?.hasBonus &&
                  !bonus?.isUsed &&
                  quotas.promotedTasks.length >= quotas.maxPromotedTasks &&
                  (!Array.isArray(item.prosseguir_managers) ||
                    !item.prosseguir_managers.length ||
                    !item.prosseguir_managers.includes(playerInfo._id))
                }
                promote={canPromote(ctx, item, item)}
                double={canDouble(ctx, item)}
                award={canAward(ctx, item)}
                onClearStatus={(item: any, status: any) => console.log('remove status', status, item)}
                data={item}
                manager={gestor}
                managerType={CheckManagerType({
                  user: { ...playerInfo, cod_etapa: userCodEtapa(ctx, playerInfo, !!item.mad) },
                  item,
                })}
                playerInfo={playerInfo}
                canEvaluate={canEvaluate(ctx, item)}
                canUpdateIndicator={canUpdateIndicator(item, playerInfo)}
                onUpdateIndicator={() => setIndicatorTask({ id: item._id, owner: item.owner })}
                isParticipantsApprover={canApproveParticipantsCount(playerInfo, item)}
                onPromote={handlePromote}
                onAccept={handleAccept}
                onReject={handleReject}
                onDouble={handleDoubled}
                onStop={handleStop}
                onFeedback={() => loadData(item._id)}
                onReload={() => loadData(item._id)}
                onAward={handleAward}
                cycle={activeStageObj}
                activeEvaluationStep={getActiveEvaluationStep}
                taskStep={getTaskStepLocal(item)}
                playerStep={getPlayerStep(ctx, item)}
                onReset={handleReset}
                reset={canReset(ctx, item)}
                getAction={getAction}
                clearGetAction={() => setGetAction(null)}
              />
            ))
          ) : (
            <VazioDaFila haviaRealizacoes={!faseSemRealizacoes && vazioPorFiltro} onLimparFiltros={limparFiltros} />
          )}
        </SecaoDaFila>

        {!!getFullCalendar && !faseSemRealizacoes && <CalendarioDoCiclo etapas={etapasDoCiclo(cycles)} agora={realDatetime} />}
      </div>

      <Outlet />

      {!!selectedGestor?.gestor && (
        <SelectGestor
          hideDupla
          player={selectedGestor}
          closeModal={() => {
            setSelectedGestor(null);
          }}
          onSubmit={(item: any) => {
            setSelectedGestor(null);
            functionsObject[functionGestor](selectedGestor.task, item._id);
          }}
        />
      )}

      {!!modalCod5?._id && (
        <ModalCod5
          task={modalCod5}
          playerInfo={playerInfo}
          onSubmit={handleSubmitCod5}
          onClose={() => setModalCod5(undefined)}
          award={canAward(ctx, modalCod5, true)}
          userCodEtapa={userCodEtapa(ctx)}
        />
      )}

      {!!showConcurrentActionModal && (
        <ModalAlert
          description={showConcurrentActionModal}
          onClose={() => {
            setShowConcurrentActionModal(undefined);
          }}
        />
      )}

      {!!indicatorTask && (
        <ModalChangeIndicator
          id={indicatorTask.id}
          owner={indicatorTask.owner}
          callback={updated => {
            const taskId = indicatorTask.id;
            setIndicatorTask(undefined);

            if (updated) loadData(taskId);
          }}
        />
      )}
    </>
  );
}
